import React, { useEffect, useState } from "react";
import { api, API, STATUS_ORDER, formatINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";

function dl(path, filename, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
  const token = localStorage.getItem("oms_token");
  fetch(`${API}${path}?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = filename; a.click(); });
}

export default function Reports() {
  const { user, cvShow } = useAuth();
  // Phase 4: commercial-hide driven by show_totals toggle (not role)
  const hide = !cvShow("show_totals");
  const [status, setStatus] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [distributorId, setDistributorId] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => { api.get("/distributors").then((r) => setDistributors(r.data)); }, []);

  async function run() {
    const params = {};
    if (status && status !== "All") params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (distributorId) params.distributor_id = distributorId;
    const { data } = await api.get("/orders", { params }); setRows(data);
  }
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const params = { status: status !== "All" ? status : "", date_from: dateFrom, date_to: dateTo, distributor_id: distributorId };
  const totalValue = rows.reduce((a, o) => a + (o.grand_total || 0), 0);
  const totalQty = rows.reduce((a, o) => a + (o.total_quantity || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Filter orders and export in CSV, XLSX or PDF.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => dl("/reports/orders.csv", "orders.csv", params)} data-testid="download-csv"><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={() => dl("/reports/orders.xlsx", "orders.xlsx", params)} data-testid="download-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> XLSX</Button>
          <Button variant="outline" onClick={() => dl("/reports/orders.pdf", "orders.pdf", params)} data-testid="download-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
        </div>
      </div>

      <Card className="p-4 border border-slate-200 shadow-none rounded-xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div><Label>Status</Label>
            <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All</SelectItem>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Distributor</Label>
            <Select value={distributorId || "all"} onValueChange={(v) => setDistributorId(v === "all" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button onClick={run} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="run-report">Run Report</Button>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <DateRangeFilter
            from={dateFrom}
            to={dateTo}
            onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
            testidPrefix="reports-daterange"
          />
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 border border-slate-200 shadow-none rounded-xl"><div className="text-xs uppercase tracking-wide text-slate-500">Orders</div><div className="heading text-3xl font-bold mt-1">{rows.length}</div></Card>
        <Card className="p-4 border border-slate-200 shadow-none rounded-xl"><div className="text-xs uppercase tracking-wide text-slate-500">Total Qty</div><div className="heading text-3xl font-bold mt-1">{totalQty}</div></Card>
        {!hide && <Card className="p-4 border border-slate-200 shadow-none rounded-xl"><div className="text-xs uppercase tracking-wide text-slate-500">Total Value</div><div className="heading text-3xl font-bold mt-1">{formatINR(totalValue)}</div></Card>}
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="p-3.5 text-left">PFI</th><th className="p-3.5 text-left">Date</th>
                <th className="p-3.5 text-left">Distributor</th><th className="p-3.5 text-right">Qty</th>
                {!hide && <th className="p-3.5 text-right">Value</th>}
                <th className="p-3.5 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? <tr><td colSpan={6} className="p-12 text-center text-slate-500">No results.</td></tr> :
                rows.map((o) => (
                  <tr key={o.id} className="table-row-hover">
                    <td className="p-3.5 mono text-[12px] font-semibold">{o.pfi_number}</td>
                    <td className="p-3.5 text-slate-700">{fmtDate(o.order_date)}</td>
                    <td className="p-3.5">{o.distributor_name}</td>
                    <td className="p-3.5 text-right">{o.total_quantity}</td>
                    {!hide && <td className="p-3.5 text-right font-medium">{formatINR(o.grand_total)}</td>}
                    <td className="p-3.5"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
